import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import portfolioData from "../../data/portfolio.json";
import projectDefaultImg from "../../image/Default Project.png";
import { ArrowUpRight, Sparkles } from "lucide-react";

// Array of 20 distinct project colors (inspired by popular design palettes)
const projectColors = [
  "#3b82f6", // blue-500
  "#10b981", // emerald-500
  "#f59e0b", // amber-500
  "#ef4444", // red-500
  "#8b5cf6", // violet-500
  "#ec4899", // pink-500
  "#06b6d4", // cyan-500
  "#f97316", // orange-500
  "#22c55e", // green-500
  "#a855f7", // purple-500
  "#14b8a6", // teal-500
  "#eab308", // yellow-500
  "#d946ef", // fuchsia-500
  "#84cc16", // lime-500
  "#0ea5e9", // sky-500
  "#fb923c", // orange-400
  "#6366f1", // indigo-500
  "#db2777", // pink-600
  "#065f46", // emerald-800
  "#be123c", // rose-700
];

const FeaturedProjectCard = ({ project, index }) => {
  const cardRef = useRef(null);
  const imgRef = useRef(null);
  const contentRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  // Pick a unique color from the array for each project based on index.
  // The color is derived by taking the modulus of the project index with the array length.
  const projectColor = projectColors[index % projectColors.length];

  const projectYear =
    project.year ||
    (project.start_date ? project.start_date.split("-")[0] : "Recent");

  useEffect(() => {
    const ctx = gsap.context(() => {
      /* Reveal Card */
      gsap.fromTo(
        cardRef.current,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: cardRef.current,
            start: "top 80%",
          },
        },
      );

      /* Image Parallax inside Card */
      if (imgRef.current) {
        gsap.to(imgRef.current, {
          yPercent: 15,
          ease: "none",
          scrollTrigger: {
            trigger: cardRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });
      }
    }, cardRef);
    return () => ctx.revert();
  }, []);

  const isEven = index % 2 === 0;

  return (
    <div
      ref={cardRef}
      className={`relative group overflow-hidden rounded-3xl glass border border-slate-200 flex flex-col bg-white shadow-lg ${
        isEven ? "md:flex-row" : "md:flex-row-reverse"
      }`}
    >
      {/* ── Image Side ── */}
      <div className="w-full md:w-1/2 lg:w-7/12 relative aspect-[4/3] md:aspect-auto overflow-hidden">
        <div
          className={`absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent z-10 ${
            isEven ? "md:bg-gradient-to-r" : "md:bg-gradient-to-l"
          }`}
        />
        <div
          className="absolute inset-0 mix-blend-color opacity-30 z-10 transition-opacity duration-500 group-hover:opacity-10"
          style={{ backgroundColor: projectColor }}
        />

        <img
          ref={imgRef}
          src={
            project.image ? `${backendUrl}${project.image}` : projectDefaultImg
          }
          alt={project.title}
          className="absolute inset-0 w-full h-[120%] -top-[10%] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />

        <div className="absolute top-6 left-6 z-20 flex gap-2">
          <span className="px-3 py-1 bg-white/90 backdrop-blur-md rounded-full text-xs font-bold text-slate-800 uppercase tracking-wider border border-slate-200">
            {projectYear}
          </span>
          <span
            className="px-3 py-1 backdrop-blur-md rounded-full text-xs font-bold uppercase tracking-wider border bg-white/90"
            style={{ borderColor: `${projectColor}50`, color: projectColor }}
          >
            {project.category}
          </span>
        </div>
      </div>

      {/* ── Content Side ── */}
      <div
        ref={contentRef}
        className="w-full md:w-1/2 lg:w-5/12 p-8 lg:p-12 flex flex-col justify-center relative z-20 bg-white"
      >
        <div
          className="absolute inset-0 z-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500"
          style={{
            background: `radial-gradient(circle at center, ${projectColor}, transparent 80%)`,
          }}
        />

        <div className="relative z-10 space-y-6">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] mb-2 uppercase text-slate-600">
              Client: {project.client}
            </p>
            <h3 className="font-display font-black text-3xl lg:text-4xl text-slate-900 leading-tight group-hover:text-gradient-primary transition-all duration-300">
              {project.title}
            </h3>
          </div>

          <div className="h-px w-full bg-slate-200 relative overflow-hidden">
            <div
              className="absolute inset-0 w-full h-full -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"
              style={{
                background: `linear-gradient(90deg, transparent, ${projectColor}, transparent)`,
              }}
            />
          </div>

          <div
            dangerouslySetInnerHTML={{ __html: project.description }}
            className="text-slate-700 leading-relaxed font-light text-base lg:text-lg"
          />

          {project.extended_data?.metrics &&
            project.extended_data.metrics.length > 0 && (
              <div className="grid grid-cols-3 gap-4 border border-slate-200 rounded-2xl p-4 bg-slate-50">
                {project.extended_data.metrics.map((metric, i) => (
                  <div key={i} className="text-center">
                    <div className="font-mono font-bold text-xl lg:text-2xl mb-1 text-slate-900">
                      {metric.value}
                    </div>
                    <div className="text-[9px] font-bold tracking-widest text-slate-600 uppercase leading-tight">
                      {metric.label}
                    </div>
                  </div>
                ))}
              </div>
            )}

          {project.tags && project.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {project.tags.map((tag, i) => (
                <span
                  key={i}
                  className="text-[10px] uppercase tracking-widest text-slate-600"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          <Link
            to={`/projects/${project.slug}`}
            className="mt-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider transition-colors duration-300 hover:text-slate-900"
            style={{ color: projectColor }}
          >
            View Full Study{" "}
            <ArrowUpRight
              size={16}
              className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </Link>
        </div>
      </div>
    </div>
  );
};

const FeaturedProjects = ({ featuredProject = [] }) => {
  const { featured } = portfolioData;

  const sectionRef = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    if (!headerRef.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current.children,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: headerRef.current,
            start: "top 80%",
          },
        },
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="section-py bg-white relative">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-b from-primary-900/5 to-transparent pointer-events-none" />

      <div className="container-fluid px-4 md:px-8 max-w-7xl mx-auto">
        <div ref={headerRef} className="max-w-2xl mb-16 md:mb-20">
          <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl text-slate-900 tracking-tight leading-none mb-4">
            {featured?.headline}{" "}
            <span className="text-gradient-primary">
              {featured?.headlineAccent}
            </span>
          </h2>
          <p className="text-slate-700 text-lg">{featured?.description}</p>
        </div>

        <div className="space-y-12 lg:space-y-20">
          {featuredProject?.map((project, i) => (
            <FeaturedProjectCard key={project.id} project={project} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProjects;
